import { useState, useEffect, useCallback } from 'react';
import { locationService } from '../../services/api';
import { toast } from '../../utils/toast';
import './AdminTable.css';

const LocationsAdmin = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await locationService.getAll();
      setLocations(response.data.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const handleEdit = (location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleSave = async (locationData) => {
    try {
      if (editingLocation) {
        await locationService.update(editingLocation.id, locationData);
        toast.success('Ubicación actualizada exitosamente');
      } else {
        await locationService.create(locationData);
        toast.success('Ubicación creada exitosamente');
      }
      setShowForm(false);
      setEditingLocation(null);
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Error al guardar ubicación');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta ubicación?')) return;

    try {
      await locationService.delete(id);
      toast.success('Ubicación eliminada exitosamente');
      loadLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Error al eliminar ubicación');
    }
  };

  if (loading) {
    return <div className="admin-loading">Cargando ubicaciones...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gestión de Ubicaciones</h2>
        <button className="btn-primary" onClick={handleCreate}>
          + Nueva Ubicación
        </button>
      </div>

      {showForm && (
        <LocationForm
          location={editingLocation}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingLocation(null);
          }}
        />
      )}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Ciudad</th>
              <th>Tipo</th>
              <th>Dirección</th>
              <th>Activa</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {locations.map(location => (
              <tr key={location.id}>
                <td>{location.name}</td>
                <td>{location.city}</td>
                <td>
                  <span className={`type-badge ${location.type}`}>
                    {location.type === 'airport' ? '✈️ Aeropuerto' : '🏢 Oficina'}
                  </span>
                </td>
                <td>{location.address}</td>
                <td>
                  <span className={`status ${location.active ? 'active' : 'inactive'}`}>
                    {location.active ? 'Sí' : 'No'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(location)}>
                      ✏️
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(location.id)}>
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {locations.length === 0 && (
          <div className="empty-state">
            No hay ubicaciones registradas. Crea una nueva para comenzar.
          </div>
        )}
      </div>
    </div>
  );
};

const LocationForm = ({ location, onSave, onCancel }) => {
  const [formData, setFormData] = useState(location || {
    name: '',
    city: '',
    type: 'office',
    address: '',
    icon: '📍',
    image: '',
    active: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="form-modal">
      <div className="form-content">
        <h3>{location ? 'Editar Ubicación' : 'Nueva Ubicación'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Ciudad *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Tipo *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="office">Oficina</option>
                <option value="airport">Aeropuerto</option>
              </select>
            </div>

            <div className="form-group">
              <label>Ícono *</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="📍"
                maxLength="2"
                required
              />
              <small>Emoji para mostrar en reservas</small>
            </div>

            <div className="form-group full-width">
              <label>Dirección</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="form-group full-width">
              <label>URL de Imagen</label>
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://ejemplo.com/imagen.jpg o /images/ubicacion.jpg"
              />
              <small>URL de la imagen de la ubicación</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span>Activa</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationsAdmin;
